import React from 'react';
import { useEventContext } from '../../../contexts/EventContext';
import { useEventDetails } from '../../../../hooks/useEventDetails';

interface Section {
  id: string;
  typeOf: string;
  sectionOption?: {
    borderStyle?: string;
    padding?: string;
    backgroundColor?: string;
    textColor?: string;
    fullWidth?: boolean;
    [key: string]: any;
  };
}

interface V1SectionContainerProps {
  section: Section;
  children: React.ReactNode;
  className?: string;
}

/**
 * V1 Section Container - FROZEN
 *
 * Provides consistent styling wrapper for V1 sections.
 * Respects section options for customization.
 *
 * DO NOT add V2 features to this component.
 */
export const V1SectionContainer: React.FC<V1SectionContainerProps> = ({
  section,
  children,
  className = ''
}) => {
  const { eventSlug, style: contextStyle } = useEventContext();
  const { data: eventDetails } = useEventDetails(eventSlug);
  
  // Get the primary color from event style or context
  const eventStyle = contextStyle || eventDetails?.style;
  const primaryColor = eventStyle?.headerColor || '#000000';

  // Set default styles that can be overridden by section options
  const getBorderStyle = () => {
    switch (section.sectionOption?.borderStyle) {
      case 'none': return 'border-0';
      case 'square': return 'rounded-none';
      default: return 'rounded-lg';
    }
  };

  const getPadding = () => {
    switch (section.sectionOption?.padding) {
      case 'small': return 'p-3';
      case 'large': return 'p-8';
      case 'none': return 'p-0';
      default: return 'p-6';
    }
  };

  // Text sections don't use container styling in V1
  if (section.typeOf === 'text') {
    return <div className={className}>{children}</div>;
  }

  // Apply full width if specified
  const widthClass = section.sectionOption?.fullWidth ? 'w-full' : '';

  return (
    <div className={`relative bg-white shadow-md ${getBorderStyle()} ${widthClass} ${className}`}>
      {/* Pseudo-element container for rounded corners */}
      <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
        {/* Top accent border */}
        <div className="h-1" style={{ backgroundColor: primaryColor }} />
      </div>
      <div
        className={`relative ${getPadding()} pt-7`}
        style={{
          backgroundColor: section.sectionOption?.backgroundColor || '',
          color: section.sectionOption?.textColor || ''
        }}
      >
        {children}
      </div>
    </div>
  );
};